import { ConfirmModal, Loading, Table, TableRowMenuCell } from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
  findUpperRecursive,
  getRecursiveArray,
} from '@/utils';
import dayjs from 'dayjs';
import { groupBy } from 'lodash-es';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import {
  useCreateExpenditurePurpose,
  useDeleteExpenditurePurpose,
  useExpenditurePurposes,
  useUpdateExpenditurePurpose,
} from '../../hooks';
import { AccountingTypeLabelModifier } from '../../models';
import {
  DEFAULT_PAGINATION_SETTING,
  fillZeroToExpenditurePurpose,
} from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { MajorMatterFormDrawer } from './MajorMatterFormDrawer';

export const MajorMatterPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const expenditurePurposes = useExpenditurePurposes({
    params: { isMajorMatter: true },
  });
  const selected = expenditurePurposes.data?.find(
    (d) =>
      drawerState.open &&
      drawerState.type === 'major-matter' &&
      drawerState.isEditing &&
      d.id === drawerState.id,
  );
  const expenditurePurposesRecursive = findUpperRecursive(
    selected?.id ?? '',
    expenditurePurposes.data ?? [],
    [],
  ).reverse();

  const { mutateAsync: createMutateAsync } = useCreateExpenditurePurpose();
  const { mutateAsync: updateMutateAsync } = useUpdateExpenditurePurpose();
  const { mutateAsync: deleteMutateAsync } = useDeleteExpenditurePurpose();
  const editPermission = useMasterTablePermission(true);
  const [majorMattersDelete, setMajorMattersDelete] = useState<string | null>(
    null,
  );

  if (expenditurePurposes.isLoading || !expenditurePurposes.data) {
    return <Loading />;
  }

  if (expenditurePurposes.isError) {
    openMessage('error', t('failed-fetch-expenditure'));
  }

  const groupedExpenditurePurposes = Object.entries(
    groupBy(
      getRecursiveArray(
        expenditurePurposes.data.filter((d) => !d.upperId),
        expenditurePurposes.data,
      ),
      'accountingId',
    ),
  )
    .flatMap(([accountingId, data]) => {
      const accounting = data.find(
        (d) => d.accountingId === accountingId,
      )?.accounting;
      if (!accounting) {
        return [];
      }
      return {
        id: accountingId,
        code: accounting.code,
        type: accounting.type,
        name: accounting.name,
        data: data.filter((v) => v.children?.length),
      };
    })
    .filter((v) => v.data.length);

  const LevelLabelModifier = {
    1: t('expenditure-purpose-level-1'),
    2: t('expenditure-purpose-level-3-1'),
  };

  return (
    <>
      <Table
        enableSticky={false}
        dataSource={groupedExpenditurePurposes}
        rowKey="id"
        columns={[
          {
            title: t('code'),
            key: 'code',
            dataIndex: 'code',
            width: TABLE_CODE_WIDTH,
          },
          {
            title: t('accounting-type'),
            key: 'type',
            dataIndex: 'type',
            width: 150,
            render: (value) => AccountingTypeLabelModifier[value],
          },
          {
            title: t('accounting-name'),
            key: 'name',
            dataIndex: 'name',
          },
        ]}
        expandable={{
          rowExpandable: (record) => record.data.length > 0,
          expandedRowRender: (record) => (
            <Table
              enableSticky={false}
              style={{ marginLeft: 80 }}
              dataSource={record.data}
              rowKey="id"
              columns={[
                {
                  title: t('code'),
                  key: 'code',
                  dataIndex: 'code',
                  width: 180,
                  onCell: () => ({
                    style: { minWidth: 180 },
                  }),
                  render: (value, record) =>
                    fillZeroToExpenditurePurpose({
                      value,
                      isMajorMatter: record.level === 2,
                    }),
                },
                {
                  title: t('level'),
                  key: 'level',
                  dataIndex: 'level',
                  width: 100,
                  render: (value: string | number) => LevelLabelModifier[value],
                },
                {
                  title: t('name'),
                  key: 'name',
                  dataIndex: 'name',
                  onCell: () => ({
                    style: { minWidth: 180 },
                  }),
                },
                {
                  title: t('start-fiscal-year'),
                  key: 'startYear',
                  dataIndex: 'startYear',
                  width: TABLE_YEAR_WIDTH,
                  onCell: () => ({
                    style: { minWidth: TABLE_YEAR_WIDTH },
                  }),
                },
                {
                  title: t('end-fiscal-year'),
                  key: 'endYear',
                  dataIndex: 'endYear',
                  width: TABLE_YEAR_WIDTH,
                  onCell: () => ({
                    style: { minWidth: TABLE_YEAR_WIDTH },
                  }),
                },
                {
                  title: t('created-datetime'),
                  key: 'createdAt',
                  dataIndex: 'createdAt',
                  width: TABLE_DATETIME_WIDTH,
                  render: (value) => datetimeFormat({ datetime: value }),
                },
                {
                  title: t('updated-datetime'),
                  key: 'updatedAt',
                  dataIndex: 'updatedAt',
                  width: TABLE_DATETIME_WIDTH,
                  render: (value) => datetimeFormat({ datetime: value }),
                },
                ...(editPermission.data
                  ? [
                      {
                        title: '',
                        key: 'actions',
                        fixed: 'right' as const,
                        width: TABLE_ROW_MENU_WIDTH,
                        onCell: () => ({
                          style: { minWidth: TABLE_ROW_MENU_WIDTH },
                        }),
                        render: (_, record) =>
                          record.level === 2 ? (
                            <TableRowMenuCell
                              onEdit={() => {
                                setDrawerState({
                                  open: true,
                                  type: 'major-matter',
                                  isEditing: true,
                                  id: record.id,
                                });
                              }}
                              items={[
                                {
                                  key: 'delete',
                                  label: t('delete'),
                                  danger: true,
                                  onClick: () =>
                                    setMajorMattersDelete(record.id),
                                },
                              ]}
                            />
                          ) : null,
                      },
                    ]
                  : []),
              ]}
              expandable={{
                rowExpandable: (record) =>
                  Boolean(record.children && record.children.length > 0),
                defaultExpandAllRows: false,
              }}
              pagination={false}
            />
          ),
          defaultExpandAllRows: false,
        }}
        pagination={DEFAULT_PAGINATION_SETTING}
      />

      <MajorMatterFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: selected
                ? {
                    ...selected,
                    period: [
                      selected.startYear
                        ? dayjs().year(selected.startYear)
                        : undefined,
                      selected.endYear
                        ? dayjs().year(selected.endYear)
                        : undefined,
                    ],
                    upperIds: [
                      expenditurePurposesRecursive[0]?.accountingId ?? '',
                      expenditurePurposesRecursive[0]?.id ?? '',
                    ],
                  }
                : {},
            }
          : { type: 'new' })}
        onSubmit={async (values) => {
          if (!drawerState.open) {
            return null;
          }
          drawerState.isEditing
            ? await updateMutateAsync({
                ...values,
                id: drawerState.id,
                isMajorMatter: true,
              })
            : await createMutateAsync({
                ...values,
                isMajorMatter: true,
              });

          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            expenditurePurposes.refetch(),
            (async () => {
              setDrawerState({ open: false });
            })(),
          ]);
        }}
      />
      <ConfirmModal
        open={majorMattersDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(majorMattersDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(majorMattersDelete);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setMajorMattersDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setMajorMattersDelete(null)}
        skipCheck
      />
    </>
  );
};
